import { useCallback, useEffect, useMemo, useState } from 'react'

interface BoundedRing {
  ring: number[]
  minLng: number
  maxLng: number
  minLat: number
  maxLat: number
}

interface ProvinceDef {
  name: string
  rings: BoundedRing[]
  minLng: number
  maxLng: number
  minLat: number
  maxLat: number
}

let cached: ProvinceDef[] | null = null
let loading: Promise<ProvinceDef[]> | null = null

function flattenRing(ring: number[][]): BoundedRing {
  const flat: number[] = []
  let minLng = Infinity
  let maxLng = -Infinity
  let minLat = Infinity
  let maxLat = -Infinity
  for (const pair of ring) {
    const lng = pair[0]
    const lat = pair[1]
    flat.push(lng, lat)
    if (lng < minLng) minLng = lng
    if (lng > maxLng) maxLng = lng
    if (lat < minLat) minLat = lat
    if (lat > maxLat) maxLat = lat
  }
  return { ring: flat, minLng, maxLng, minLat, maxLat }
}

function loadProvinces(): Promise<ProvinceDef[]> {
  if (cached) return Promise.resolve(cached)
  if (loading) return loading
  loading = fetch(`${process.env.PUBLIC_URL || ''}/iran.json`)
    .then((r) => {
      if (!r.ok) throw new Error(`iran.json fetch failed: ${r.status}`)
      return r.json()
    })
    .then((geo: any) => {
      const defs: ProvinceDef[] = []
      for (const f of geo.features as any[]) {
        const name = f.properties.name as string
        const rings: BoundedRing[] = []
        if (f.geometry.type === 'Polygon') {
          rings.push(flattenRing(f.geometry.coordinates[0]))
        } else if (f.geometry.type === 'MultiPolygon') {
          for (const poly of f.geometry.coordinates) {
            rings.push(flattenRing(poly[0]))
          }
        }
        let minLng = Infinity
        let maxLng = -Infinity
        let minLat = Infinity
        let maxLat = -Infinity
        for (const r of rings) {
          if (r.minLng < minLng) minLng = r.minLng
          if (r.maxLng > maxLng) maxLng = r.maxLng
          if (r.minLat < minLat) minLat = r.minLat
          if (r.maxLat > maxLat) maxLat = r.maxLat
        }
        defs.push({ name, rings, minLng, maxLng, minLat, maxLat })
      }
      cached = defs
      return defs
    })
  return loading
}

function pointInRing(lng: number, lat: number, flat: number[]): boolean {
  let inside = false
  const n = flat.length
  for (let i = 0, j = n - 2; i < n; j = i, i += 2) {
    const xi = flat[i]
    const yi = flat[i + 1]
    const xj = flat[j]
    const yj = flat[j + 1]
    const intersect =
      (yi > lat) !== (yj > lat) &&
      lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi
    if (intersect) inside = !inside
  }
  return inside
}

function resolveSync(
  defs: ProvinceDef[],
  lat: number,
  lng: number,
): string | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  for (const d of defs) {
    if (lng < d.minLng || lng > d.maxLng || lat < d.minLat || lat > d.maxLat) {
      continue
    }
    for (const r of d.rings) {
      if (
        lng < r.minLng ||
        lng > r.maxLng ||
        lat < r.minLat ||
        lat > r.maxLat
      ) {
        continue
      }
      if (pointInRing(lng, lat, r.ring)) return d.name
    }
  }
  return null
}

export const UNKNOWN_PROVINCE = '__unknown__'

export function useProvinces() {
  const [defs, setDefs] = useState<ProvinceDef[] | null>(cached)
  useEffect(() => {
    if (defs) return
    let mounted = true
    loadProvinces().then((d) => {
      if (mounted) setDefs(d)
    })
    return () => {
      mounted = false
    }
  }, [defs])

  const resolve = useCallback(
    (lat: number, lng: number): string | null => {
      if (!defs) return null
      return resolveSync(defs, lat, lng)
    },
    [defs],
  )

  const provinceNames = useMemo(
    () => (defs ? defs.map((d) => d.name).sort((a, b) => a.localeCompare(b, 'fa')) : []),
    [defs],
  )

  return { ready: !!defs, provinceNames, resolve }
}
